import { Box } from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React from "react";

import { monospaceStyle, panelShadow } from "../../theme";
import { CountryFlag } from "../common/CountryFlag";
import Tag from "../Detail/Tag";
import { quiet } from "../Detail/claimCells";
import { codeWords } from "../Detail/format";
import { RelationRow } from "./productRelations";

// The tooltip's panel: the raised surface with a hairline and the shadow of
// a panel over a chart, as the analytics charts draw theirs
export const RECORD_TOOLTIP_SLOTS = {
  tooltip: {
    sx: {
      maxWidth: 320,
      p: 0,
      backgroundColor: "background.raised",
      color: "text.primary",
      border: 1,
      borderColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.14),
      boxShadow: (theme: Theme) => panelShadow(theme, 0.55),
    },
  },
  arrow: {
    sx: {
      color: "background.raised",
      "&::before": {
        border: 1,
        borderColor: (theme: Theme) => alpha(theme.palette.chart.ink, 0.14),
      },
    },
  },
} as const;

// Severities set apart in the warning colour
const GRAVE = new Set(["major", "critical"]);

export interface RecordTooltipProps {
  kind: "programme" | "incident" | "facility";
  row: RelationRow;
  // The hue the chart draws the kind in
  hue: string;
  // Every role the record plays towards the product
  roles: string[];
  // What the date line is called ("Period", "Date", "Operating") and says;
  // no line without a date
  dateLabel: string;
  date: string | null;
  // How the date is known, where it is not to the day ("known to the year")
  dateNote: string | null;
  // Where a facility stands in words, where more than its country is known,
  // and how exactly its place on the map is known ("exact location")
  location?: { address: string | null; precision: string };
  // The relation in a sentence, under the name
  summary?: string | null;
}

// The most lines of a summary a tooltip shows
const SUMMARY_LINES = 4;

// One line of the details: its name in the quiet colour, its value beside
const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <>
    <Box component="dt" sx={{ ...quiet, m: 0 }}>
      {label}
    </Box>
    <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
      {children}
    </Box>
  </>
);

/**
 * What a mark of the timeline or a point of the map says on hover: its
 * kind with a sample of its mark, its status or severity, the name and
 * types of its record, then its role, date, place, countries, stated
 * quantity and sources.
 */
const RecordTooltip: React.FC<RecordTooltipProps> = ({
  kind,
  row,
  hue,
  roles,
  dateLabel,
  date,
  dateNote,
  location,
  summary,
}) => {
  const status = (row.status ?? []).map(codeWords).join(", ");
  const types = (row.types ?? []).map(codeWords).join(", ");
  const countries = row.countries ?? [];
  const sources = row.sources.length;

  return (
    <Box sx={{ px: 1.5, py: 1.25, fontSize: "0.75rem", lineHeight: 1.45 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        <Box
          component="span"
          aria-hidden
          sx={
            kind === "programme"
              ? { width: 14, height: 6, borderRadius: "3px", backgroundColor: hue }
              : { width: 8, height: 8, borderRadius: "50%", backgroundColor: hue }
          }
        />
        <Box
          component="span"
          sx={{
            ...quiet,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {kind}
        </Box>
        <Box sx={{ ml: "auto" }}>
          {kind !== "incident" && status && <Tag>{status}</Tag>}
          {kind === "incident" && row.severity && (
            <Tag tone={GRAVE.has(row.severity) ? "warning" : "default"}>
              {codeWords(row.severity)}
            </Tag>
          )}
        </Box>
      </Box>
      <Box sx={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.35 }}>
        {row.entity.name}
      </Box>
      {types && <Box sx={{ ...quiet, mt: 0.25 }}>{types}</Box>}
      {summary && (
        <Box
          sx={{
            mt: 0.75,
            display: "-webkit-box",
            WebkitLineClamp: SUMMARY_LINES,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {summary}
        </Box>
      )}
      <Box
        component="dl"
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1.5,
          rowGap: 0.5,
          m: 0,
          mt: 1,
          pt: 1,
          borderTop: 1,
          borderColor: (theme) => alpha(theme.palette.chart.ink, 0.1),
        }}
      >
        {roles.length > 0 && <Detail label="Role">{roles.join(", ")}</Detail>}
        {date && (
          <Detail label={dateLabel}>
            <Box component="span" sx={monospaceStyle}>
              {date}
            </Box>
            {dateNote && <Box sx={quiet}>{dateNote}</Box>}
          </Detail>
        )}
        {location && (
          <Detail label="Location">
            {location.address ? (
              <>
                {location.address}
                <Box sx={quiet}>{location.precision}</Box>
              </>
            ) : (
              location.precision
            )}
          </Detail>
        )}
        {countries.length > 0 && (
          <Detail label={countries.length === 1 ? "Country" : "Countries"}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {countries.map((code) => (
                <Box
                  key={code}
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  <CountryFlag code={code} size={12} round />
                  <Box component="span" sx={monospaceStyle}>
                    {code}
                  </Box>
                </Box>
              ))}
            </Box>
          </Detail>
        )}
        {row.quantityText && <Detail label="Quantity">{row.quantityText}</Detail>}
        {sources > 0 && (
          <Detail label="Sources">
            {sources}
            {row.latest && (
              <Box component="span" sx={quiet}>
                {" · latest "}
                <Box component="span" sx={monospaceStyle}>
                  {row.latest}
                </Box>
              </Box>
            )}
          </Detail>
        )}
      </Box>
    </Box>
  );
};

export default RecordTooltip;
